<template>
  <form-block :title="tr('Budget')">
    <info-text v-if="!isAvailable">
      {{ tr('You have set a campaign budget, so the budget at ad set level is not available.') }}
    </info-text>
    <template v-else>
      <budget-editor
        ref="budgetEditor"
        v-model="entity.settings.adset_budget"
        :period="entity.settings.adset_budget_period"
        level="adset"
        :label="tr('Ad set budget')"
        :currency-code="entity.adAccountCurrency ? entity.adAccountCurrency.code : 'EUR'"
        :ad-account-currency-symbol="entity.adAccountCurrency ? entity.adAccountCurrency.symbol : null"
        :ad-account-id="entity.ad_account ? entity.ad_account.id : null"
        :disabled="disabled"
        :period-disabled="disabled || !entity.isNew"
        :lifetime-start-time="entity.settings.adset_start_time"
        :lifetime-end-time="entity.settings.adset_end_time"
        :lifetime-lifespan-max-days="entity.settings.adset_lifespan_max_days"
        @update:period="val => entity.settings.adset_budget_period = val"
        @blur="onBudgetBlur"
      >
        <template #after-input>
          <div v-if="budgetPeriod === 'lifetime'" class="pt-2">
            <div class="grid gap-4 sm:grid-cols-2">
              <div class="flex flex-col gap-1.5">
                <ui-label>{{ tr('Start date') }}</ui-label>
                <date-time-input
                  v-model="entity.settings.adset_start_time"
                  :disabled="disabled || !entity.isNew"
                  :min-date="new Date()"
                />
              </div>
              <div class="flex flex-col gap-1.5">
                <ui-label>{{ tr('End date') }}</ui-label>
                <date-time-input
                  v-model="entity.settings.adset_end_time"
                  :disabled="disabled || !entity.isNew"
                  :min-date="new Date()"
                />
              </div>
            </div>
          </div>
        </template>

        <template #info-texts="{ invalidBudgetInfoText, isBudgetRequestRunning, minBudgetInfoText }">
          <transition-expand>
            <info-text v-if="invalidLifetimeRange" type="error">
              <div v-html="invalidLifetimeRangeInfoText" />
            </info-text>
            <info-text v-else-if="hasPastLifetimeRange()" type="error">
              {{ pastLifetimeRangeInfoText() }}
            </info-text>
            <info-text v-else-if="shouldShowBudgetError" type="error">
              <div v-html="invalidBudgetInfoText" />
            </info-text>
            <info-text v-else :icon="isBudgetRequestRunning ? 'loader' : null">
              <div v-html="minBudgetInfoText" />
            </info-text>
          </transition-expand>
        </template>
      </budget-editor>

      <transition-expand>
        <div v-if="budgetPeriod === 'lifetime'" class="flex flex-col gap-1.5">
          <ui-label for="adset-lifespan-max-days">
            {{ tr('Ad lifespan (max. days)') }} ({{ tr('optional') }})
          </ui-label>
          <ui-input
            id="adset-lifespan-max-days"
            v-model="entity.settings.adset_lifespan_max_days"
            type="number"
            min="1"
            :max="adsetLifespanMaxDaysMaxValue"
            step="1"
            class="w-24"
            :disabled="disabled"
          />
        </div>
      </transition-expand>

      <div class="flex flex-col gap-1.5">
        <ui-label for="adset-max-active-ads">
          {{ tr('Maximum number of posts promoted at once') }} ({{ tr('optional') }})
        </ui-label>
        <ui-input
          id="adset-max-active-ads"
          v-model="entity.settings.max_active_ads"
          type="number"
          min="1"
          step="1"
          class="w-24"
          :disabled="disabled"
        />
      </div>

      <slot />
    </template>
  </form-block>
</template>

<script>
import RuleFormBlockMixin from '@/mixins/RuleForm/Block.js';
import BudgetBlockMixin from '@/mixins/RuleForm/Blocks/Budget.js';
import BudgetEditor from '@/ui/components/BudgetEditor.vue';
import DateTimeInput from '@/ui/components/DateTimeInput.vue';
import InfoText from '@/ui/components/InfoText.vue';
import TransitionExpand from '@/ui/components/TransitionExpand.vue';

export default {
  name: 'AdsetBudgetBlock',
  components: {
    BudgetEditor,
    DateTimeInput,
    InfoText,
    TransitionExpand,
  },
  mixins: [ RuleFormBlockMixin, BudgetBlockMixin ],
  computed: {
    isCampaignLevel() {
      return !this.isAdsetLevel;
    },
    isAdsetLevel() {
      return this.isAvailable;
    },
    isAvailable() {
      return !this.entity.output_campaign.budget;
    },
    adsetLifespanMaxDaysMaxValue() {
      if (this.budgetPeriod !== 'lifetime') {
        return null;
      }

      const result = Math.floor((this.entity.settings.adset_end_time.getTime() - this.entity.settings.adset_start_time.getTime()) / 1000 / 86400);
      if (isNaN(result) || result < 1) {
        return 1;
      }

      return result;
    },
  },
  created() {
    if (this.isAvailable && !this.entity.settings.adset_budget_period) {
      this.entity.settings.adset_budget_period = this.budgetPeriods[0].machine_name;
    }
  },
  methods: {
    validate() {
      if (this.isAvailable) {
        if (!this.entity.settings.adset_budget) {
          this.error(this.tr('Ad set budget is required because you have not set a campain level budget!'));
          return false;
        }

        if (!this.validateBudget()) {
          return false;
        }

        if (
          this.entity.settings.max_active_ads !== null
          && this.entity.settings.max_active_ads !== ''
          && this.entity.settings.max_active_ads < 1
        ) {
          this.error(this.tr('Maximum number of active ads at the same time cannot be less than one!'));
          return false;
        }
      }

      return true;
    },
  },
};
</script>
